// الموقع لحظة النقرة فقط — لا تتبّع ولا مراقبة مستمرة (لا watchPosition).
//
// قرار المالك D-2 مرفوض رفضًا كاملًا: لا تُجمع ولا تُرسل أي بيانات جهاز — لا userAgent ولا
// platform ولا مقاس الشاشة ولا أي معرّف. ما يُرسل هو ما تطلبه القاعدة المعتمدة وحده:
// الإحداثيات عند توفّرها، أو حالة غياب الموقع وسببها عند تعذّره.
//
// رفض إذن الموقع لا يمنع تسجيل الحضور إطلاقًا: الدالة لا ترفض أبدًا، بل تُرجع جسمًا صالحًا
// في كل الحالات. والقاعدتان المعتمدتان في الخادم محفوظتان هنا:
//   · خط العرض والطول يُرسلان معًا أو لا يُرسلان.
//   · غياب الموقع المعلَن يعني بلا إحداثيات على الإطلاق.
import { Capacitor, registerPlugin } from '@capacitor/core';
import type { TapBody } from '../lib/api.ts';
import { isNativeApp } from '../lib/platform.ts';

/** مهلة قصيرة: النقرة لا تنتظر الموقع طويلًا — الحضور أهم من إحداثياته. */
const GEO_TIMEOUT_MS = 8000;

const DENIED_AR: Record<number, string> = {
  1: 'رفض المتصفح إذن الموقع',
  2: 'تعذّر تحديد الموقع على هذا الجهاز',
  3: 'انتهت مهلة تحديد الموقع',
};

const missing = (reason: string): TapBody => ({ locationMissing: true, locationDeniedReason: reason });

/**
 * جسم النقرة كما يُرسل. لا يرفض أبدًا، ولا يحتوي وقتًا (الوقت من الخادم وحده)،
 * ولا معرّف موظف (صاحب الجلسة)، ولا بيانات جهاز (D-2).
 */
export async function tapLocation(): Promise<TapBody> {
  if (isNativeApp() && Capacitor.isPluginAvailable('LocationGuard')) return nativeTapLocation();
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    return missing('الموقع غير مدعوم في هذا المتصفح');
  }
  /**
   * **الموقع لا يُتاح إلا على رابطٍ آمن** — قاعدةُ المتصفّحات لا إعدادُ البرنامج: تحديد
   * الموقع «سياقٌ محميّ» (secure context) لا يعمل إلا على https أو localhost.
   *
   * فمن يفتح البرنامج من هاتفه على رابط الشبكة المحلّية (`http://192.168…`) يمنعه
   * المتصفّح **قبل أن يسأله الإذن أصلًا** — فيُسجَّل «رفض المتصفّح إذن الموقع»، ويُفهم
   * أن الموظف رفض وهو لم يُسأل (قرار المالك 22/09/2026: «ليه لما سجّلت من الموبايل
   * مخدش لوكيشن؟»). فيُقال السببُ على وجهه ولا يُتّهم أحد.
   *
   * والعلاج ليس هنا: رابطٌ بـhttps للبرنامج — وهو ما يُجهَّز على الخادم.
   */
  if (typeof window !== 'undefined' && window.isSecureContext === false) {
    return missing('الرابط مش آمن (http) — المتصفّح بيمنع الموقع إلا على https');
  }
  return new Promise<TapBody>((resolve) => {
    let settled = false;
    const done = (b: TapBody): void => { if (!settled) { settled = true; resolve(b); } };
    try {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const { latitude, longitude, accuracy } = pos.coords;
          // إحداثية واحدة بلا الأخرى مرفوضة في الخادم — فإن نقصت إحداهما يُعلَن غياب الموقع
          if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
            done(missing('تعذّر تحديد الموقع على هذا الجهاز'));
            return;
          }
          const body: TapBody = { locationMissing: false, gpsLat: latitude, gpsLon: longitude };
          // numeric(6,1) في القاعدة: الحد الأقصى 99999.9 — والدقّة قد تغيب فلا تُرسل حينها
          if (Number.isFinite(accuracy) && accuracy >= 0 && accuracy < 100000) {
            body.gpsAccuracyM = Math.round(accuracy * 10) / 10;
          }
          done(body);
        },
        (err) => done(missing(DENIED_AR[err?.code as number] ?? 'تعذّر تحديد الموقع')),
        { enableHighAccuracy: true, timeout: GEO_TIMEOUT_MS, maximumAge: 0 },
      );
    } catch {
      done(missing('تعذّر تحديد الموقع'));
    }
  });
}

/**
 * **من تطبيق أندرويد: الموقع ومعه علامة «مزيّف»** (المراجعة الشاملة الثالثة، والمالك 03/10/2026: «أ» — التطبيق يكشف برامج
 * تزوير الموقع زي Fake GPS ويرفض البصمة). موقع المتصفّح داخل التطبيق يُخفي ذلك، والإضافة الأصلية LocationGuard تقرؤه من
 * أندرويد نفسه. وتُعلَّم البصمة «من التطبيق» — والخادم يرفض المزيّفة، ويرفض بصمة المتصفّح إن جعل المالك البصمة للتطبيق وحده.
 * والقاعدة نفسها: لا ترفض أبدًا — تعذّر الموقع ⇒ «بلا موقع» بسببه.
 */
const LocationGuard = registerPlugin<{ current(): Promise<{ lat: number; lon: number; accuracy?: number; mock?: boolean }> }>('LocationGuard');

async function nativeTapLocation(): Promise<TapBody> {
  const REASON: Record<string, string> = {
    DENIED: 'رفض إذن الموقع للتطبيق', OFF: 'الموقع (GPS) مقفول على الموبايل', TIMEOUT: 'انتهت مهلة تحديد الموقع',
  };
  try {
    const r = await LocationGuard.current();
    if (!Number.isFinite(r.lat) || !Number.isFinite(r.lon)) return { ...missing('تعذّر تحديد الموقع على هذا الجهاز'), fromApp: true };
    const body: TapBody = { locationMissing: false, gpsLat: r.lat, gpsLon: r.lon, fromApp: true, mockLocation: r.mock === true };
    if (Number.isFinite(r.accuracy) && r.accuracy! >= 0 && r.accuracy! < 100000) body.gpsAccuracyM = Math.round(r.accuracy! * 10) / 10;
    return body;
  } catch (e) {
    const code = (e as { code?: string } | null)?.code ?? '';
    return { ...missing(REASON[code] ?? 'تعذّر تحديد الموقع'), fromApp: true };
  }
}
